import { memo, useMemo } from 'react';
import {
  Bookmark,
  BookmarkCheck,
  CheckSquare,
  CornerUpLeft,
  Copy,
  Forward,
  Link2,
  MailOpen,
  MoreHorizontal,
  Pencil,
  Pin,
  SmilePlus,
  Settings2,
  Trash2,
} from 'lucide-react';
import { Dropdown, IconButton, type DropdownItem, cn } from '@cyphex/ui';
import type { Message } from '@cyphex/types';
/** Deep import: the `@/features/apps` barrel would pull the admin panels in. */
import { useAppMessageActions } from '@/features/apps/hooks/useAppMessageActions';
import { useMessagingPolicy } from '@/hooks/useMyPolicy';
import type { MessageExtras } from '../../types';
import { QuickReactions } from './QuickReactions';
import { ReminderMenu } from './ReminderMenu';

interface MessageActionsBarProps {
  message: Message;
  isOwn: boolean;
  extras: MessageExtras;
  onReply: () => void;
  onReact: () => void;
  onForward: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onPin: () => void;
  onCopy: () => void;
}

/** Hover toolbar. Only mounted for the hovered row, never for the whole list. */
export const MessageActionsBar = memo(function MessageActionsBar({
  message,
  isOwn,
  extras,
  onReply,
  onReact,
  onForward,
  onEdit,
  onDelete,
  onPin,
  onCopy,
}: MessageActionsBarProps) {
  const reacted = useMemo(
    () => new Set(message.reactions.filter((r) => r.reacted).map((r) => r.emoji)),
    [message.reactions],
  );

  // App-contributed entries. Empty on a workspace with no message actions
  // registered, which is why they are appended rather than given a section.
  const appActions = useAppMessageActions(message.id);

  // ADMIN MESSAGING POLICY. An administrator who switched editing or
  // self-deletion off gets a menu without those items rather than two entries
  // that answer 403 — the API refuses either way (services/policyGate.ts), this
  // is only about not lying to the person holding the mouse.
  const { allowEditMessages, allowDeleteOwnMessages } = useMessagingPolicy();

  const items = useMemo<DropdownItem[]>(
    () => [
      { id: 'copy', label: 'Copy text', icon: Copy, onSelect: onCopy },
      {
        id: 'permalink',
        label: 'Copy link to message',
        icon: Link2,
        onSelect: () => extras.onCopyLink(message),
      },
      { id: 'forward', label: 'Forward', icon: Forward, onSelect: onForward },
      {
        id: 'save',
        label: message.isSaved ? 'Remove from saved' : 'Save this message',
        icon: message.isSaved ? BookmarkCheck : Bookmark,
        onSelect: () => extras.onToggleSaved(message),
        separatorBefore: true,
      },
      {
        id: 'unread',
        label: 'Mark unread from here',
        icon: MailOpen,
        onSelect: () => extras.onMarkUnread(message),
      },
      {
        id: 'pin',
        label: message.isPinned ? 'Unpin from chat' : 'Pin to chat',
        icon: Pin,
        onSelect: onPin,
      },
      // Only where a task can actually be made — see `MessageExtras.onCreateTask`.
      ...(extras.onCreateTask === undefined
        ? []
        : [
            {
              id: 'task',
              label: 'Create a task from this',
              icon: CheckSquare,
              onSelect: () => extras.onCreateTask?.(message),
            },
          ]),
      {
        id: 'quick-reactions',
        label: 'Customise quick reactions…',
        icon: Settings2,
        onSelect: extras.onCustomiseQuickReactions,
        separatorBefore: true,
      },
      ...(isOwn && allowEditMessages
        ? [
            { id: 'edit', label: 'Edit', icon: Pencil, shortcut: 'E', onSelect: onEdit, separatorBefore: true },
          ]
        : []),
      ...(isOwn && allowDeleteOwnMessages
        ? [
            {
              id: 'delete',
              label: 'Delete',
              icon: Trash2,
              destructive: true,
              onSelect: onDelete,
              // The rule normally sits on Edit. With Edit gone, Delete inherits
              // it, or the destructive item would sit flush against "Customise
              // quick reactions…".
              separatorBefore: !allowEditMessages,
            },
          ]
        : []),
      // Last, always. First-party actions are what people reach for; an app
      // must not be able to push "Delete" further down the menu.
      ...appActions.items,
    ],
    [
      allowDeleteOwnMessages,
      allowEditMessages,
      appActions.items,
      extras,
      isOwn,
      message,
      onCopy,
      onDelete,
      onEdit,
      onForward,
      onPin,
    ],
  );

  return (
    <div
      className={cn(
        'absolute top-0 z-sticky hidden items-center gap-0.5 rounded-lg border border-border bg-card p-0.5 shadow-dropdown',
        'group-hover/message:flex group-focus-within/message:flex',
        /* Beside the bubble, on its outer edge — never over it and never at the
           far margin. Anchoring above the bubble instead put the bar straight
           over the sender's name and timestamp on the first message of a group;
           Teams keeps that line readable while the bar is up, and so does this. */
        isOwn ? 'right-full mr-1.5' : 'left-full ml-1.5',
      )}
    >
      <QuickReactions
        emoji={extras.quickReactions}
        reacted={reacted}
        onReact={(emoji) => extras.onQuickReact(message, emoji)}
        className="mr-0.5 border-r border-border pr-1"
      />
      <IconButton icon={SmilePlus} label="Add reaction" size="sm" variant="ghost" onClick={onReact} />
      <IconButton icon={CornerUpLeft} label="Reply" size="sm" variant="ghost" onClick={onReply} />
      <ReminderMenu
        message={message}
        onSet={(request) => extras.onSetReminder(message, request)}
        onClear={() => extras.onClearReminder(message)}
      />
      <IconButton
        icon={message.isSaved ? BookmarkCheck : Bookmark}
        label={message.isSaved ? 'Remove from saved' : 'Save this message'}
        size="sm"
        variant="ghost"
        onClick={() => extras.onToggleSaved(message)}
      />
      <Dropdown
        items={items}
        align="end"
        trigger={<IconButton icon={MoreHorizontal} label="More actions" size="sm" variant="ghost" />}
      />
      {appActions.dialog}
    </div>
  );
});
